import { ChevronDown } from "lucide-react";

export interface SelectOption {
	value: string;
	label: string;
}

interface SelectProps {
	/** Label shown to the left of the select (omit when using inside a form-field) */
	label?: string;
	/** Controlled value (ignored when `register` is provided) */
	value?: string;
	/** Controlled onChange (ignored when `register` is provided) */
	onChange?: (value: string) => void;
	options: SelectOption[];
	/** Makes the wrapper fill 100% of its parent (for form fields; default false = inline-fit for filter bars) */
	fullWidth?: boolean;
	className?: string;
	disabled?: boolean;
	/** React Hook Form register result — takes priority over value/onChange */
	register?: ReturnType<
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(...args: any[]) => {
			ref: React.RefCallback<HTMLSelectElement>;
			onChange: React.ChangeEventHandler<HTMLSelectElement>;
			onBlur: React.ChangeEventHandler<HTMLSelectElement>;
			name: string;
		}
	>;
}

/**
 * Reusable select dropdown with chevron icon.
 *
 * Works in two modes:
 * 1. **Controlled**: pass `value` + `onChange` (e.g., filter bars)
 * 2. **React Hook Form**: pass `register()` — takes priority
 *
 * Uses existing globals.css classes: `.pl-filter`, `.pl-select-wrap`, `.pl-select-icon`
 */
export function Select({
	label,
	value,
	onChange,
	options,
	fullWidth,
	className = "",
	disabled,
	register,
}: SelectProps) {
	// RHF mode: spread register props onto the native select
	const rhfProps = register ?? {};

	return (
		<div
			className={`pl-filter ${className}`}
			style={fullWidth ? { width: "100%" } : undefined}
		>
			{label && <label>{label}</label>}
			<div
				className="pl-select-wrap"
				style={fullWidth ? { width: "100%" } : undefined}
			>
				<select
					value={register ? undefined : value}
					onChange={register ? undefined : (e) => onChange?.(e.target.value)}
					className="input"
					style={{ width: fullWidth ? "100%" : "max-content" }}
					disabled={disabled}
					{...rhfProps}
				>
					{options.map((opt) => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</select>
				<ChevronDown className="pl-select-icon" size={14} />
			</div>
		</div>
	);
}
